import DrawersStyles from '@Styles/drawers.scss';
import { CircularProgress } from '@mui/material';
import _cloneDeep from 'lodash/cloneDeep';
import _groupBy from 'lodash/groupBy';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _sortBy from 'lodash/sortBy';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, {
    Layer,
    MapRef,
    NavigationControl,
    Source,
    ViewStateChangeEvent,
} from 'react-map-gl';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { IEntity } from '@Data/entities/Interfaces';
import { IZoomToBoundsId } from '@Data/folders/Interfaces';
import { setZoomToFolderBoundsIdState } from '@Data/folders/Reducer';
import { selectedProjectState } from '@Data/projects/Reducer';
import WorkbenchesApi from '@Data/workbenches/Api';
import { info } from '@Services/notification/NotificationServices';
import { RESIZE_REFRESH_DELAY } from '@Views/QualityControl/components/InProgress/SvgViewer/constants';
import Styles from './MapComponents.scss';
import { MapStyle } from './MapStyle';
import {
    LayerType,
    getBoundingBoxFromMouseEvent,
    getLayerId,
    getMapMenuWidthInPixels,
    getWfsData,
    mapMapboxFeature,
} from './Utils';

interface MapComponentProps {
    onFeatureClick: any;
    popupComponent?: any;
    isPopupOpen: boolean;
    sourceData: any;
    selectedEntity: IEntity;
    isLoading: boolean;
    baseLayerId: string;
    flyToEntityId?: string;
    initialViewState: any;
}

const MapComponent = (props: MapComponentProps) => {
    const mapRef = useRef<MapRef>();
    const selectedZoomToBoundsFolderId: IZoomToBoundsId = useSelector(
        setZoomToFolderBoundsIdState
    );
    const unclusteredZoomLevel = 11;
    const [interactiveLayers, setInteractiveLayers] = useState<any>({
        clusterLayerId: '',
        pointLayerId: '',
    });
    const selectedProject = useSelector(selectedProjectState);
    const { width, height, ref } = useResizeDetector<HTMLDivElement>({
        refreshMode: 'debounce',
        refreshRate: RESIZE_REFRESH_DELAY,
    });
    const {
        onFeatureClick,
        popupComponent,
        isPopupOpen,
        sourceData,
        selectedEntity,
        isLoading,
        baseLayerId,
        flyToEntityId,
        initialViewState,
    } = props;

    useEffect(() => {
        mapRef.current?.resize();
    }, [width, height]);

    useEffect(() => {
        if (
            !_isNil(selectedZoomToBoundsFolderId) &&
            !_isNil(selectedZoomToBoundsFolderId.folderIds)
        ) {
            const queryObj = {
                folder_id: selectedZoomToBoundsFolderId.folderId,
                folder_ids: selectedZoomToBoundsFolderId.folderIds,
            };

            zoomToBounds(queryObj.folder_id, queryObj.folder_ids);
        }
    }, [selectedZoomToBoundsFolderId]);

    const zoomToBounds = (folderId, folderIds: Array<any>) => {
        const getBbox = async () => {
            let valid_ids = folderIds.filter(
                (f) => !_isNil(f) && f !== '' && f !== 'undefined'
            );
            if (valid_ids.length === 0) {
                return;
            }
            const response = await getWfsData(
                valid_ids,
                selectedProject.project_id
            );
            if (response.data && response.data.features.length > 0) {
                const basePadding = 25;
                const drawerPadding =
                    getMapMenuWidthInPixels(DrawersStyles.contentsDrawerWidth) +
                    basePadding;
                if (mapRef.current) {
                    mapRef.current.fitBounds(response.data.bbox, {
                        padding: {
                            top: basePadding,
                            left: basePadding,
                            right: selectedEntity ? drawerPadding : basePadding,
                            bottom: basePadding,
                        },
                        duration: 50,
                    });
                }
            } else {
                info('There are no poles to zoom to');
            }
        };
        getBbox();
    };

    useEffect(() => {
        if (!_isNil(flyToEntityId) && mapRef.current && sourceData) {
            const entity = sourceData.features.find(
                (f) => f.properties.entity_version_id === flyToEntityId
            );
            if (entity && mapRef.current) {
                const currentZoom = mapRef.current.getZoom();

                mapRef.current.easeTo({
                    duration: 50,
                    center: entity.geometry.coordinates,
                    zoom:
                        currentZoom === 1
                            ? unclusteredZoomLevel + 1
                            : currentZoom,
                });
            }
        }
    }, [flyToEntityId]);

    const handleMapFeatureEvent = (e, callback) => {
        const bbox = getBoundingBoxFromMouseEvent(e);
        const features = mapRef.current.queryRenderedFeatures(bbox);

        if (features.length > 0) {
            callback(features, bbox);
        }
    };

    const handleMouseMoveEvent = (e) => {
        if (mapRef.current) {
            mapRef.current.getCanvas().style.cursor = '';
            handleMapFeatureEvent(e, () => {
                mapRef.current.getCanvas().style.cursor = 'pointer';
            });
        }
    };

    const handleClick = (e) => {
        handleMapFeatureEvent(e, (features) => {
            const feature = mapMapboxFeature(features[0]);
            if (feature.properties.cluster) {
                mapRef.current
                    .getSource(feature.layer.source)
                    // eslint-disable-next-line
                    //@ts-ignore
                    .getClusterExpansionZoom(
                        feature.properties.cluster_id,
                        function (err, zoom) {
                            if (err || !mapRef.current) return;

                            mapRef.current.easeTo({
                                center: features[0].geometry.coordinates,
                                zoom: zoom,
                                duration: 50,
                            });
                        }
                    );
            } else {
                onFeatureClick(features);
            }
        });
    };

    const sourceComponent = useMemo(() => {
        if (_isNil(sourceData)) {
            setInteractiveLayers({ clusterLayerId: '', pointLayerId: '' });
            return null;
        } else {
            const clusterLayerId = getLayerId(baseLayerId, LayerType.CLUSTER);
            const pointLayerId = getLayerId(baseLayerId, LayerType.POINT);

            setInteractiveLayers({
                clusterLayerId: clusterLayerId,
                pointLayerId: pointLayerId,
            });

            return (
                <Source
                    id={`src${baseLayerId}`}
                    key={`src${baseLayerId}`}
                    type="geojson"
                    data={sourceData}
                    cluster={true}
                    clusterRadius={35}
                    clusterMaxZoom={unclusteredZoomLevel}
                >
                    {/* cluster circles */}
                    <Layer
                        id={clusterLayerId}
                        type="circle"
                        filter={['has', 'point_count']}
                        paint={{
                            'circle-color': [
                                'step',
                                ['get', 'point_count'],
                                '#51bbd6',
                                5,
                                '#f1f075',
                                20,
                                '#f28cb1',
                            ],
                            'circle-radius': [
                                'step',
                                ['get', 'point_count'],
                                20,
                                5,
                                30,
                                20,
                                40,
                            ],
                        }}
                    />

                    {/* cluster number */}
                    <Layer
                        id={getLayerId(baseLayerId, LayerType.CLUSTER_NUMBER)}
                        type="symbol"
                        filter={['has', 'point_count']}
                        layout={{
                            'text-field': '{point_count_abbreviated}',
                            'text-size': 12,
                        }}
                    />

                    {/* unclustered entities */}
                    <Layer
                        id={pointLayerId}
                        type="circle"
                        filter={['!', ['has', 'point_count']]}
                        paint={{
                            'circle-color': [
                                'coalesce',
                                ['get', 'colour'],
                                'black',
                            ],
                            'circle-radius': 4,
                            'circle-stroke-width': 1,
                            'circle-stroke-color': '#fff',
                        }}
                    />

                    {/* selected entity */}
                    <Layer
                        id={getLayerId(baseLayerId, LayerType.SELECTED_ENTITY)}
                        type="circle"
                        filter={[
                            '==',
                            ['get', 'entity_version_id'],
                            selectedEntity
                                ? selectedEntity.entity_version_id
                                : 'anUnMatchableStrinG',
                        ]}
                        paint={{
                            'circle-color': [
                                'coalesce',
                                ['get', 'colour'],
                                'black',
                            ],
                            'circle-radius': 4,
                            'circle-stroke-width': 3,
                            'circle-stroke-color': 'red',
                        }}
                    />
                </Source>
            );
        }
    }, [baseLayerId, sourceData, selectedEntity]);

    const handleMoveEnd = (e: ViewStateChangeEvent) => {
        WorkbenchesApi.setViewState(e.viewState);
    };

    return (
        <React.Fragment>
            <div style={{ display: 'flex', flexGrow: 1 }} ref={ref}>
                <Map
                    id="project-map"
                    mapStyle={MapStyle}
                    ref={mapRef}
                    mapLib={maplibregl}
                    initialViewState={initialViewState}
                    style={{ display: 'flex', flexGrow: 1 }}
                    onMouseMove={handleMouseMoveEvent}
                    onClick={handleClick}
                    interactiveLayerIds={
                        !_isNil(sourceData)
                            ? [
                                  interactiveLayers.clusterLayerId,
                                  interactiveLayers.pointLayerId,
                              ]
                            : []
                    }
                    onMoveEnd={handleMoveEnd}
                >
                    <NavigationControl
                        position="bottom-right"
                        showZoom
                        visualizePitch
                    />
                    {/* <LayerController position='bottom-right' onClick={(e) => console.log(e)} /> */}

                    {sourceComponent}

                    {isPopupOpen && (
                        <React.Fragment>{popupComponent}</React.Fragment>
                    )}
                </Map>
            </div>

            {isLoading && (
                <div className={Styles.mapLoadingContainer}>
                    <CircularProgress />
                </div>
            )}
        </React.Fragment>
    );
};

export default MapComponent;
